/**
 * The shared reader of backend error responses. Every service answers errors
 * as an RFC 9457 problem (application/problem+json) with a stable error code,
 * a ready-to-show error message in the UI language (`detail`, `title`), public
 * parameters, permission-gated hints and field errors; older services still
 * answer their own shapes. readProblem turns all of them into one Problem, so
 * no caller parses an error text.
 */

/** The id-based reference of a field error whose field was not in the request. */
export interface ProblemFieldTarget {
  resource: string;
  id: number;
  field: string;
}

/** One field error: where (pointer / key, parameter or target), what (code) and the short text. */
export interface ProblemFieldError {
  /** JSON Pointer into the request body, e.g. '/lines/3/quantity'. */
  pointer?: string;
  /** The pointer as dotted key, e.g. 'lines.3.quantity' (pointerToKey). */
  key?: string;
  /** The query parameter to blame, instead of a pointer. */
  parameter?: string;
  /** The record and field to blame when it was not in the request. */
  target?: ProblemFieldTarget;
  code: string;
  /** Short text for next to the input, without a field label. */
  detail: string;
  params?: Record<string, unknown>;
}

/** A backend error response in one shape, whatever shape the service answered. */
export interface Problem {
  /**
   * The HTTP status of the problem; 0 for a GraphQL error, whose envelope
   * answers 200 and whose extensions name no status — switch on `code`.
   */
  status: number;
  /** The error code to switch on. */
  code: string;
  title: string;
  /** The sentence to show. */
  detail: string;
  /** Public raw parameter values. */
  params: Record<string, unknown>;
  /** Permission-gated hints the caller may see; never part of a text. */
  hints: Record<string, unknown>;
  fieldErrors: ProblemFieldError[];
  /** The request id that finds the log line (the toast action „Fehler-ID kopieren“). */
  requestId?: string;
}

/** Whether a Content-Type is JSON: application/json and every +json type (RFC 6839). */
export function isJsonContentType(contentType: string | null | undefined): boolean {
  if (!contentType) return false;
  const mediaType = contentType.split(';', 1)[0].trim().toLowerCase();
  if (mediaType === 'application/json') return true;
  return /^[a-z0-9!#$&^_.-]+\/[a-z0-9!#$&^_.+-]+\+json$/.test(mediaType);
}

/**
 * Turns a JSON Pointer (RFC 6901) into the dotted key the forms bind to:
 * '/lines/3/quantity' → 'lines.3.quantity'; '~1' and '~0' unescape to '/' and
 * '~'. The empty pointer (the whole body) is the empty key.
 */
export function pointerToKey(pointer: string): string {
  if (pointer === '') return '';
  const path = pointer.startsWith('/') ? pointer.slice(1) : pointer;
  return path
    .split('/')
    .map((segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~'))
    .join('.');
}

/**
 * Reads the problem of a failed request from an axios-like error
 * ({ response: { status, data, headers } }): the problem shape and every
 * older shape the services answer. A fetch Response is read from its status
 * and headers only (its body cannot be read synchronously — read it with
 * readProblemBody). An error without a response that carries the errors of a
 * GraphQL envelope ({ errors: [{ message, extensions }] }, erp's
 * GraphQLRequestError) is read from them (readGraphqlErrors). undefined when
 * e carries neither; never throws.
 */
export function readProblem(e: unknown): Problem | undefined {
  try {
    if (typeof e !== 'object' || e === null) return undefined;
    if (typeof Response !== 'undefined' && e instanceof Response) {
      return readProblemBody(e.status, undefined, e.headers);
    }
    const response = (e as { response?: unknown }).response;
    if (typeof response !== 'object' || response === null) {
      return readGraphqlErrors((e as { errors?: unknown }).errors);
    }
    const { status, data, headers } = response as {
      status?: unknown;
      data?: unknown;
      headers?: unknown;
    };
    return readProblemBody(
      typeof status === 'number' ? status : Number.NaN,
      data,
      headers as Headers | Record<string, string> | undefined
    );
  } catch {
    return undefined;
  }
}

/**
 * Reads the problem of a response from its status, its parsed body and its
 * headers. The body may also be the raw response text: it is parsed when the
 * Content-Type is JSON (or, without a Content-Type, when it looks like a JSON
 * object). A body that is no JSON (an HTML page of a proxy, an empty body)
 * yields a problem with the status only — this never throws. undefined only
 * when neither the status nor the body carries a valid HTTP status.
 */
export function readProblemBody(
  status: number,
  body: unknown,
  headers?: Headers | Record<string, string>
): Problem | undefined {
  try {
    const data = parseBody(body, headerValue(headers, 'Content-Type'));
    const httpStatus = validStatus(status) ?? validStatus(data?.status);
    if (httpStatus === undefined) return undefined;
    const problem = statusOnlyProblem(httpStatus, headerValue(headers, REQUEST_ID_HEADER));
    if (data) fillProblem(problem, data);
    return problem;
  } catch {
    const httpStatus = validStatus(status);
    return httpStatus === undefined ? undefined : statusOnlyProblem(httpStatus, undefined);
  }
}

/**
 * Reads the problem of a GraphQL response from its errors array, as erp's
 * transports render every error (BLP/nx#2637, spec §7.4):
 *
 *   {"message": <detail>, "path": …,
 *    "extensions": {"code", "title", "requestId", "errors"?, "hints"?}}
 *
 * `message` is the detail in the UI language, `extensions.errors` the field
 * errors in the problem shape (a pointer points into the mutation field's
 * input argument, a target names a record the input did not carry) and
 * `extensions.hints` the hints the caller may see. The first error that names
 * a code is read: one write is one mutation field, and the field errors of
 * another field's input would bind to the wrong inputs. `status` is 0 (see
 * Problem.status). undefined when no error names a code — e.g. the client's
 * own "no data" error; never throws.
 */
export function readGraphqlErrors(errors: unknown): Problem | undefined {
  try {
    if (!Array.isArray(errors)) return undefined;
    for (const entry of errors) {
      if (!isPlainObject(entry) || !isPlainObject(entry.extensions)) continue;
      const extensions = entry.extensions;
      const code = text(extensions.code);
      if (code === undefined) continue;
      const problem = statusOnlyProblem(0, text(extensions.requestId));
      problem.code = code;
      problem.title = text(extensions.title) ?? '';
      problem.detail = text(entry.message) ?? '';
      if (isPlainObject(extensions.hints)) problem.hints = { ...extensions.hints };
      if (Array.isArray(extensions.errors)) {
        problem.fieldErrors = extensions.errors.flatMap((fieldError) => problemFieldError(fieldError, code));
      }
      return problem;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

type JsonObject = Record<string, unknown>;

const REQUEST_ID_HEADER = 'X-Request-ID';

/**
 * The top-level members of the problem shape, its aliases and the old flat
 * shapes ({status, code, message}, {error}, {error, code}, {detail},
 * {success, message}, {errors}). Every other top-level member of a body
 * without `hints` is read as a hint, e.g. `permission` of erp's authz refusal.
 */
const BODY_MEMBERS = new Set([
  'type',
  'status',
  'code',
  'title',
  'detail',
  'instance',
  'params',
  'hints',
  'errors',
  'requestId',
  'message',
  'error',
  'success',
]);

/**
 * The members of the nested error object of erp's former transition alias
 * (`apictx.Fail*`, reads, aggregates — erp answers the problem members only
 * since PR 3 of BLP/nx#2637). Every other member is a hint: erp's alias spread
 * its hints there (e.g. `conflictingTemplate`).
 */
const ERROR_MEMBERS = new Set(['code', 'message', 'fields', 'duplicateFields']);

/** An old flat `{"error": "unauthenticated"}` string that is an error code, not a sentence. */
const CODE_TOKEN = /^[a-z][a-z0-9_]*$/;

function statusOnlyProblem(status: number, requestId: string | undefined): Problem {
  const problem: Problem = {
    status,
    code: '',
    title: '',
    detail: '',
    params: {},
    hints: {},
    fieldErrors: [],
  };
  if (requestId) problem.requestId = requestId;
  return problem;
}

/** Fills problem from a JSON body of any shape; the newer member always wins. */
function fillProblem(problem: Problem, data: JsonObject): void {
  const nested = isPlainObject(data.error) ? data.error : undefined;
  const flatError = text(data.error);
  problem.code =
    text(data.code) ??
    text(nested?.code) ??
    (flatError !== undefined && CODE_TOKEN.test(flatError) ? flatError : '');
  problem.title = text(data.title) ?? '';
  problem.detail =
    text(data.detail) ?? text(nested?.message) ?? text(data.message) ?? flatError ?? '';
  problem.params = isPlainObject(data.params) ? { ...data.params } : {};
  problem.hints = isPlainObject(data.hints)
    ? { ...data.hints }
    : {
        ...otherMembers(data, BODY_MEMBERS),
        ...(nested ? otherMembers(nested, ERROR_MEMBERS) : {}),
      };
  problem.fieldErrors = readFieldErrors(data, nested, problem.code);
  const requestId = text(data.requestId);
  if (requestId) problem.requestId = requestId;
}

function readFieldErrors(
  data: JsonObject,
  nested: JsonObject | undefined,
  code: string
): ProblemFieldError[] {
  // Problem shape: errors[{pointer | parameter | target, code, detail, params}].
  if (Array.isArray(data.errors)) {
    return data.errors.flatMap((entry) => problemFieldError(entry, code));
  }
  if (nested) {
    // erp reads / aggregates: error.fields[{path, code, message}].
    if (Array.isArray(nested.fields)) {
      return nested.fields.flatMap((entry) => pathFieldError(entry, code));
    }
    // erp /v1: error.fields {key: message}, error.duplicateFields [key].
    if (isPlainObject(nested.fields) || Array.isArray(nested.duplicateFields)) {
      return keyedFieldErrors(nested, code);
    }
  }
  // master validation: errors {field: [{field, message, code, args}]}.
  if (isPlainObject(data.errors)) return masterFieldErrors(data.errors, code);
  return [];
}

function problemFieldError(entry: unknown, code: string): ProblemFieldError[] {
  if (!isPlainObject(entry)) return [];
  const pointer = typeof entry.pointer === 'string' ? entry.pointer : undefined;
  const parameter = text(entry.parameter);
  const target = fieldTarget(entry.target);
  if (pointer === undefined && parameter === undefined && target === undefined) return [];
  const fieldError: ProblemFieldError = {
    code: text(entry.code) ?? code,
    detail: text(entry.detail) ?? text(entry.message) ?? '',
  };
  if (pointer !== undefined) {
    fieldError.pointer = pointer;
    fieldError.key = pointerToKey(pointer);
  }
  if (parameter !== undefined) fieldError.parameter = parameter;
  if (target !== undefined) fieldError.target = target;
  if (isPlainObject(entry.params)) fieldError.params = { ...entry.params };
  return [fieldError];
}

function fieldTarget(value: unknown): ProblemFieldTarget | undefined {
  if (!isPlainObject(value)) return undefined;
  const resource = text(value.resource);
  const field = text(value.field);
  const id = value.id;
  if (resource === undefined || field === undefined) return undefined;
  if (typeof id !== 'number' || !Number.isFinite(id)) return undefined;
  return { resource, id, field };
}

function pathFieldError(entry: unknown, code: string): ProblemFieldError[] {
  if (!isPlainObject(entry)) return [];
  const key = text(entry.path);
  if (key === undefined) return [];
  return [{ key, code: text(entry.code) ?? code, detail: text(entry.message) ?? '' }];
}

function keyedFieldErrors(nested: JsonObject, code: string): ProblemFieldError[] {
  const fieldErrors: ProblemFieldError[] = [];
  const seen = new Set<string>();
  if (isPlainObject(nested.fields)) {
    for (const [key, message] of Object.entries(nested.fields)) {
      fieldErrors.push({ key, code, detail: text(message) ?? '' });
      seen.add(key);
    }
  }
  if (Array.isArray(nested.duplicateFields)) {
    for (const key of nested.duplicateFields) {
      if (typeof key !== 'string' || key === '' || seen.has(key)) continue;
      fieldErrors.push({ key, code, detail: '' });
      seen.add(key);
    }
  }
  return fieldErrors;
}

function masterFieldErrors(errors: JsonObject, code: string): ProblemFieldError[] {
  const fieldErrors: ProblemFieldError[] = [];
  for (const [key, entries] of Object.entries(errors)) {
    for (const entry of Array.isArray(entries) ? entries : [entries]) {
      if (typeof entry === 'string') {
        fieldErrors.push({ key, code, detail: entry });
      } else if (isPlainObject(entry)) {
        const fieldError: ProblemFieldError = {
          key,
          code: text(entry.code) ?? code,
          detail: text(entry.message) ?? '',
        };
        if (isPlainObject(entry.args)) fieldError.params = { ...entry.args };
        fieldErrors.push(fieldError);
      }
    }
  }
  return fieldErrors;
}

/** The body as a JSON object; undefined for an empty, non-JSON or non-object body. */
function parseBody(body: unknown, contentType: string | undefined): JsonObject | undefined {
  if (typeof body === 'string') {
    const raw = body.trim();
    if (raw === '') return undefined;
    if (contentType ? !isJsonContentType(contentType) : !raw.startsWith('{')) return undefined;
    try {
      const parsed: unknown = JSON.parse(raw);
      return isPlainObject(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }
  return isPlainObject(body) ? body : undefined;
}

/** A header of a Headers, an axios headers object or a plain record, case-insensitive. */
function headerValue(headers: unknown, name: string): string | undefined {
  if (typeof headers !== 'object' || headers === null) return undefined;
  const get = (headers as { get?: unknown }).get;
  if (typeof get === 'function') {
    const value: unknown = get.call(headers, name);
    if (typeof value === 'string' && value !== '') return value;
  }
  const wanted = name.toLowerCase();
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === wanted && typeof value === 'string' && value !== '') return value;
  }
  return undefined;
}

function validStatus(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 100 && value <= 599
    ? value
    : undefined;
}

function otherMembers(object: JsonObject, known: ReadonlySet<string>): JsonObject {
  const members: JsonObject = {};
  for (const [key, value] of Object.entries(object)) {
    if (!known.has(key) && value !== undefined) members[key] = value;
  }
  return members;
}

/**
 * A plain JSON object — not an array, a Blob, an ArrayBuffer or any other
 * instance. Checked by the prototype chain, so an object of another realm
 * (an iframe, a worker's clone) counts too.
 */
function isPlainObject(value: unknown): value is JsonObject {
  if (typeof value !== 'object' || value === null) return false;
  const proto: object | null = Object.getPrototypeOf(value);
  return proto === null || Object.getPrototypeOf(proto) === null;
}

/** A non-empty string, else undefined. */
function text(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}
